import type { GameCommand } from '../core/types';

export interface PointerSample {
  readonly x: number;
  readonly y: number;
  readonly time: number;
}

export interface SwipeConfig {
  readonly minimumDistance: number;
  readonly maximumDuration: number;
  readonly dominantAxisRatio: number;
}

export const SWIPE_CONFIG = {
  minimumDistance: 40,
  maximumDuration: 350,
  dominantAxisRatio: 1.2,
} as const satisfies SwipeConfig;

export function isSlideKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return normalized === 'arrowdown' || normalized === 's';
}

export function classifyGesture(
  start: PointerSample,
  end: PointerSample,
  config: SwipeConfig = SWIPE_CONFIG,
): GameCommand | undefined {
  const elapsed = end.time - start.time;
  if (elapsed < 0 || elapsed > config.maximumDuration) return undefined;

  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const absoluteX = Math.abs(deltaX);
  const absoluteY = Math.abs(deltaY);

  if (Math.max(absoluteX, absoluteY) < config.minimumDistance) return { type: 'primary' };
  if (absoluteX >= absoluteY * config.dominantAxisRatio) {
    return { type: 'moveLane', direction: deltaX < 0 ? -1 : 1 };
  }
  if (absoluteY >= absoluteX * config.dominantAxisRatio) {
    return deltaY < 0 ? { type: 'jump' } : { type: 'slide' };
  }
  return undefined;
}

export function commandForKey(key: string): GameCommand | undefined {
  switch (key.toLowerCase()) {
    case 'arrowleft':
    case 'a':
      return { type: 'moveLane', direction: -1 };
    case 'arrowright':
    case 'd':
      return { type: 'moveLane', direction: 1 };
    case 'arrowup':
    case 'w':
      return { type: 'jump' };
    case 'arrowdown':
    case 's':
      return { type: 'slide' };
    case 'enter':
    case ' ':
      return { type: 'primary' };
    case 'escape':
      return { type: 'pause' };
    default:
      return undefined;
  }
}
